import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Award, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

export default function CertificateVerify() {
  const { certificateNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await api.get(`/certificate/verify/${certificateNumber}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Certificate not found' : 'Verification failed');
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [certificateNumber]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <Loader2 size={32} className="animate-spin text-white" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="certificate-verify-page">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <Award size={48} className="mx-auto text-amber-400 mb-4" />
          <h1 className="text-2xl font-bold text-white">Certificate Verification</h1>
          <p className="text-slate-400 mt-2">Verify the authenticity of a certificate</p>
        </div>

        <Card className="border-0 shadow-2xl overflow-hidden">
          {error ? (
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Certificate</h2>
              <p className="text-slate-500 mb-4">{error}</p>
              <p className="text-sm text-slate-400">Certificate Number: {certificateNumber}</p>
            </CardContent>
          ) : data?.valid ? (
            <CardContent className="p-0">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white text-center">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={32} />
                </div>
                <h2 className="text-xl font-bold">Certificate Verified</h2>
                <p className="text-emerald-100 text-sm mt-1">This certificate is authentic</p>
              </div>

              {/* Details */}
              <div className="p-6 space-y-4">
                <div className="text-center mb-6">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Certified To</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-1">{data.certificate.user_name}</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Course</span>
                    <span className="font-medium text-slate-900">{data.certificate.course_title}</span>
                  </div>
                  {data.certificate.instructor && (
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Instructor</span>
                      <span className="font-medium text-slate-900">{data.certificate.instructor}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Issued By</span>
                    <span className="font-medium text-slate-900">{data.certificate.issued_by}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Completion Date</span>
                    <span className="font-medium text-slate-900">
                      {new Date(data.certificate.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-500">Certificate ID</span>
                    <Badge variant="outline" className="font-mono">{data.certificate.certificate_number}</Badge>
                  </div>
                </div>

                {/* Tenant Logo */}
                {data.tenant?.logo_url && (
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-center gap-3">
                    <img src={data.tenant.logo_url} alt={data.tenant.name} className="h-10 object-contain" />
                    <span className="text-sm text-slate-500">{data.tenant.name}</span>
                  </div>
                )}
              </div>
            </CardContent>
          ) : null}
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2">
            <ArrowLeft size={14} /> Back to Tokomoe
          </Link>
        </div>
      </div>
    </div>
  );
}
